import {
  PREVENTIVE_CARE_ACK_ACTIONS,
  decide,
  type PreventiveCareAckAction,
  type PreventiveCareAcknowledgementResponse,
  type PreventiveCareRecommendationResponse,
} from '@emr/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { acknowledgePreventiveCareRecommendation } from '../../../api/preventiveCare.ts';
import { errorMessage, isContractMismatch } from '../../../api/client.ts';
import { useGrants } from '../../../auth/sessionContext.ts';
import { FailureNotice } from '../../../components/AsyncState.tsx';
import {
  Badge,
  Button,
  DetailRow,
  ErrorText,
  type BadgeTone,
} from '../../../components/primitives.tsx';
import { summarizeText } from '../../../components/summarizeText.ts';
import { formatCalendarDate, formatDateTime } from '../../../lib/dateFormat.ts';

const SUMMARY_CHARS = 320;

const STATUS_TONE: Record<PreventiveCareAckAction, BadgeTone> = {
  acknowledged: 'accent',
  ordered: 'success',
  dismissed: 'neutral',
};

/**
 * One currently-open preventive-care gap (`PreventiveCareTab.tsx`). Cards, not a `ChartTable` row —
 * matching `NoteCard.tsx`'s own precedent, since a recommendation carries a non-directive summary, a
 * full DSI-transparency disclosure, and up to three real actions, none of which fit a table cell.
 *
 * **A gap the clinician has already acted on reads differently from one that has not been touched
 * yet**, per this feature's own task framing: `latestAcknowledgement` is `null` until a decision has
 * been recorded for this `ruleId`, and stays non-null afterward even though the recommendation
 * itself keeps recomputing as "open" — this route never writes to `immunizations`
 * (`preventiveCareRoutes.ts`'s own header), so acting on a gap does not close it the way recording a
 * real immunization would. A warning-toned border and a "needs review" badge mark the untouched
 * case; an already-decided one gets a status badge and its own last-decision detail row instead, but
 * the action controls stay available either way — a clinician revisiting a prior "dismissed" call at
 * a later encounter is a real, expected flow, not a state to lock out.
 */
export function PreventiveCareCard({
  patientId,
  recommendation,
  latestAcknowledgement,
}: {
  readonly patientId: string;
  readonly recommendation: PreventiveCareRecommendationResponse;
  readonly latestAcknowledgement: PreventiveCareAcknowledgementResponse | null;
}) {
  const { t } = useTranslation('patientChartTabs');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const [sourceExpanded, setSourceExpanded] = useState(false);
  const [note, setNote] = useState('');

  const { summary, truncated } = summarizeText(recommendation.summary, SUMMARY_CHARS);
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ['patient', patientId, 'preventiveCareRecommendations'],
    });
    void queryClient.invalidateQueries({
      queryKey: ['patient', patientId, 'preventiveCareAcknowledgements'],
    });
  };

  const acknowledge = useMutation({
    mutationFn: (action: PreventiveCareAckAction) =>
      acknowledgePreventiveCareRecommendation(patientId, recommendation.ruleId, {
        action,
        note: note.trim().length > 0 ? note.trim() : undefined,
      }),
    onSuccess: () => {
      setNote('');
      invalidate();
    },
  });

  const needsReview = latestAcknowledgement === null;

  return (
    <li
      className={`flex flex-col gap-3 rounded-control border p-3 text-sm sm:p-4 ${
        needsReview ? 'border-warning' : 'border-line'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-ink">{recommendation.vaccineLabel}</span>
        {needsReview ? (
          <Badge tone="warning">{t('preventiveCare.status.needsReview')}</Badge>
        ) : (
          <Badge tone={STATUS_TONE[latestAcknowledgement.action]}>
            {t(`preventiveCare.status.${latestAcknowledgement.action}`)}
          </Badge>
        )}
      </div>

      <p className="m-0 whitespace-pre-wrap break-words text-ink [overflow-wrap:anywhere]">
        {summaryExpanded ? recommendation.summary : summary}
        {truncated && !summaryExpanded ? '…' : ''}
      </p>
      {truncated ? (
        <button
          type="button"
          onClick={() => {
            setSummaryExpanded((value) => !value);
          }}
          className="self-start text-sm font-medium text-accent-text hover:underline"
        >
          {summaryExpanded ? t('preventiveCare.showLess') : t('preventiveCare.showMore')}
        </button>
      ) : null}

      {!needsReview ? (
        <div className="flex flex-col gap-1 rounded-control border border-line-strong bg-raised p-2 text-xs text-ink-muted">
          <span>
            {t('preventiveCare.lastRecorded', {
              action: t(`preventiveCare.status.${latestAcknowledgement.action}`),
              date: formatDateTime(latestAcknowledgement.createdAt),
            })}
          </span>
          {latestAcknowledgement.note !== null ? (
            <span className="text-ink">{latestAcknowledgement.note}</span>
          ) : null}
        </div>
      ) : null}

      {/* DSI transparency (`BACKLOG.md` C12, HTI-1 §170.315(b)(11)(iv)(A)): a compact, collapsed-by-
          default disclosure, never omitted — the recommendation's own `source` object is rendered in
          full here, not summarized down to a display string, since the criterion is that this
          information reach an identified user, not that some of it does. */}
      <div className="border-t border-line pt-2">
        <button
          type="button"
          onClick={() => {
            setSourceExpanded((value) => !value);
          }}
          aria-expanded={sourceExpanded}
          className="self-start text-sm font-medium text-accent-text hover:underline"
        >
          {sourceExpanded ? t('preventiveCare.source.hide') : t('preventiveCare.source.show')}
        </button>
        {sourceExpanded ? (
          <div className="mt-2 flex flex-col gap-3">
            <dl className="m-0">
              <DetailRow label={t('preventiveCare.source.citation')}>
                {recommendation.source.bibliographicCitation}
              </DetailRow>
              <DetailRow label={t('preventiveCare.source.developer')}>
                {recommendation.source.interventionDeveloper}
              </DetailRow>
              <DetailRow label={t('preventiveCare.source.funding')}>
                {recommendation.source.fundingSource}
              </DetailRow>
              <DetailRow label={t('preventiveCare.source.releaseDate')}>
                {formatCalendarDate(recommendation.source.releaseDate)}
              </DetailRow>
              <DetailRow label={t('preventiveCare.source.revisionDate')}>
                {formatCalendarDate(recommendation.source.revisionDate)}
              </DetailRow>
              <DetailRow label={t('preventiveCare.source.dataElementsUsed')}>
                {recommendation.source.dataElementsUsed.join(', ')}
              </DetailRow>
              <DetailRow label={t('preventiveCare.source.mostRecentAdministered')}>
                {recommendation.basisData.mostRecentAdministeredDate === null
                  ? t('preventiveCare.source.none')
                  : formatCalendarDate(recommendation.basisData.mostRecentAdministeredDate)}
              </DetailRow>
              <DetailRow label={t('preventiveCare.source.matchedImmunizations')}>
                {recommendation.basisData.matchedImmunizationVaccines.length === 0
                  ? t('preventiveCare.source.none')
                  : recommendation.basisData.matchedImmunizationVaccines.join(', ')}
              </DetailRow>
            </dl>
            {/* The explicit true/false disclosure HTI-1 requires even (especially) when every
                answer is "no" — `preventiveCareRules.ts`'s own header on why documenting the
                absence of use is itself the required disclosure, not an omitted field. */}
            <div>
              <p className="m-0 mb-1 text-xs font-medium text-ink-muted">
                {t('preventiveCare.source.dataElementsHeading')}
              </p>
              <ul className="m-0 grid list-none grid-cols-1 gap-x-4 gap-y-1 p-0 text-xs text-ink-muted sm:grid-cols-2">
                <DataElementFlag
                  label={t('preventiveCare.source.usesRaceOrEthnicity')}
                  used={recommendation.source.usesRaceOrEthnicity}
                  t={t}
                />
                <DataElementFlag
                  label={t('preventiveCare.source.usesLanguage')}
                  used={recommendation.source.usesLanguage}
                  t={t}
                />
                <DataElementFlag
                  label={t('preventiveCare.source.usesSexualOrientationOrGenderIdentity')}
                  used={recommendation.source.usesSexualOrientationOrGenderIdentity}
                  t={t}
                />
                <DataElementFlag
                  label={t('preventiveCare.source.usesSex')}
                  used={recommendation.source.usesSex}
                  t={t}
                />
                <DataElementFlag
                  label={t('preventiveCare.source.usesDateOfBirth')}
                  used={recommendation.source.usesDateOfBirth}
                  t={t}
                />
                <DataElementFlag
                  label={t('preventiveCare.source.usesSocialDeterminantsOfHealth')}
                  used={recommendation.source.usesSocialDeterminantsOfHealth}
                  t={t}
                />
                <DataElementFlag
                  label={t('preventiveCare.source.usesHealthStatusAssessments')}
                  used={recommendation.source.usesHealthStatusAssessments}
                  t={t}
                />
              </ul>
            </div>
          </div>
        ) : null}
      </div>

      {mayWrite ? (
        <div className="flex flex-col gap-2 border-t border-line pt-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
            {t('preventiveCare.noteLabel')}
            <textarea
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
              rows={2}
              placeholder={t('preventiveCare.notePlaceholder')}
              className="w-full rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            {PREVENTIVE_CARE_ACK_ACTIONS.map((action) => (
              <Button
                key={action}
                variant="secondary"
                pending={acknowledge.isPending && acknowledge.variables === action}
                disabled={acknowledge.isPending && acknowledge.variables !== action}
                onClick={() => {
                  acknowledge.mutate(action);
                }}
              >
                {t(`preventiveCare.actions.${action}`)}
              </Button>
            ))}
          </div>
          {acknowledge.error !== null ? (
            isContractMismatch(acknowledge.error) ? (
              <FailureNotice error={acknowledge.error} />
            ) : (
              <ErrorText message={errorMessage(acknowledge.error)} />
            )
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function DataElementFlag({
  label,
  used,
  t,
}: {
  readonly label: string;
  readonly used: boolean;
  readonly t: TFunction<'patientChartTabs'>;
}) {
  return (
    <li>
      {label}: {used ? t('preventiveCare.source.yes') : t('preventiveCare.source.no')}
    </li>
  );
}
